/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Web Search API Integration
 * 
 * Provides unified interface for multiple search APIs:
 * - Tavily: AI-optimized search for RAG workflows
 * - Brave Search: Independent index, privacy-focused
 * - Serper: Google-powered, cost-effective
 */

export interface SearchResult {
  title: string
  url: string
  snippet: string
  publishedDate?: string
  score?: number
  source?: string
}

export interface SearchOptions {
  numResults?: number
  searchDepth?: 'basic' | 'advanced'
  includeImages?: boolean
  includeDomains?: string[]
  excludeDomains?: string[]
}

/**
 * Search using Tavily API
 * Optimized for AI agents and RAG workflows
 * 
 * @param query - Search query
 * @param apiKey - Tavily API key
 * @param options - Search options
 * @returns Array of search results
 */
export async function searchTavily(
  query: string,
  apiKey: string,
  options: SearchOptions = {}
): Promise<SearchResult[]> {
  const { numResults = 5, searchDepth = 'basic', includeDomains, excludeDomains } = options

  try {
    const response = await fetch('https://api.tavily.com/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        api_key: apiKey,
        query,
        search_depth: searchDepth,
        max_results: numResults,
        include_domains: includeDomains,
        exclude_domains: excludeDomains,
        include_answer: false,
        include_raw_content: false,
      }),
    })

    if (!response.ok) {
      throw new Error(`Tavily API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    return (data.results || []).map((result: any) => ({
      title: result.title,
      url: result.url,
      snippet: result.content,
      score: result.score,
      source: 'tavily',
    }))
  } catch (error) {
    console.error('Tavily search error:', error)
    throw error
  }
}

/**
 * Search using Brave Search API
 * Independent index, privacy-focused
 * 
 * @param query - Search query
 * @param apiKey - Brave Search API key
 * @param options - Search options
 * @returns Array of search results
 */
export async function searchBrave(
  query: string,
  apiKey: string,
  options: SearchOptions = {}
): Promise<SearchResult[]> {
  const { numResults = 5 } = options

  try {
    const params = new URLSearchParams({
      q: query,
      count: numResults.toString(),
    })

    const response = await fetch(`https://api.search.brave.com/res/v1/web/search?${params}`, {
      headers: {
        'Accept': 'application/json',
        'X-Subscription-Token': apiKey,
      },
    })

    if (!response.ok) {
      throw new Error(`Brave Search API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    return (data.web?.results || []).map((result: any) => ({
      title: result.title,
      url: result.url,
      snippet: result.description,
      publishedDate: result.age,
      source: 'brave',
    }))
  } catch (error) {
    console.error('Brave search error:', error)
    throw error
  }
}

/**
 * Search using Serper API
 * Google-powered, cost-effective
 * 
 * @param query - Search query
 * @param apiKey - Serper API key
 * @param options - Search options
 * @returns Array of search results
 */
export async function searchSerper(
  query: string,
  apiKey: string,
  options: SearchOptions = {}
): Promise<SearchResult[]> {
  const { numResults = 5 } = options

  try {
    const response = await fetch('https://google.serper.dev/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': apiKey,
      },
      body: JSON.stringify({
        q: query,
        num: numResults,
      }),
    })

    if (!response.ok) {
      throw new Error(`Serper API error: ${response.status} ${response.statusText}`)
    }

    const data = await response.json()

    return (data.organic || []).map((result: any) => ({
      title: result.title,
      url: result.link,
      snippet: result.snippet,
      source: 'serper',
    }))
  } catch (error) {
    console.error('Serper search error:', error)
    throw error
  }
}

/**
 * Unified search function that tries available search APIs in order
 *
 * @param query - Search query
 * @param apiKeys - Object containing available API keys
 * @param options - Search options
 * @returns Array of search results
 */
export async function search(
  query: string,
  apiKeys: {
    tavily?: string
    brave?: string
    serper?: string
  },
  options: SearchOptions = {}
): Promise<SearchResult[]> {
  // Try Tavily first (AI-optimized)
  if (apiKeys.tavily) {
    try {
      return await searchTavily(query, apiKeys.tavily, options)
    } catch (error) {
      console.warn('Tavily search failed, trying next provider:', error)
    }
  }

  // Try Brave second (independent index)
  if (apiKeys.brave) {
    try {
      return await searchBrave(query, apiKeys.brave, options)
    } catch (error) {
      console.warn('Brave search failed, trying next provider:', error)
    }
  }

  // Try Serper last (cheapest)
  if (apiKeys.serper) {
    try {
      return await searchSerper(query, apiKeys.serper, options)
    } catch (error) {
      console.warn('Serper search failed:', error)
      throw error
    }
  }

  throw new Error('No search API keys configured. Please add at least one search API key in Settings.')
}

/**
 * Check if any search API is configured
 */
export function hasSearchApiKey(apiKeys: { tavily?: string; brave?: string; serper?: string }): boolean {
  return !!(apiKeys.tavily || apiKeys.brave || apiKeys.serper)
}

/**
 * Get the web search tool definition for LLM function calling
 */
export function getWebSearchTool() {
  return {
    type: 'function' as const,
    function: {
      name: 'web_search',
      description: 'Search the web for current information, news, facts, and data. Use this when you need up-to-date information that may not be in your training data. Returns a list of relevant web pages with titles, URLs, and snippets.',
      parameters: {
        type: 'object' as const,
        properties: {
          query: {
            type: 'string',
            description: 'The search query. Be specific and use keywords that will return relevant results.',
          },
          num_results: {
            type: 'number',
            description: 'Number of search results to return (default: 5, max: 10)',
            default: 5,
          },
        },
        required: ['query'],
      },
    },
  }
}

